import { useState, useEffect } from 'react';
import {
  makeStyles,
  tokens,
  Card,
  Title2,
  Body1,
  Text,
  Button,
  Badge,
  Spinner,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Field,
  Textarea,
  Tab,
  TabList,
  MessageBar,
  MessageBarBody,
  Divider,
} from '@fluentui/react-components';
import {
  CheckmarkRegular,
  DismissRegular,
  PersonRegular,
  CalendarRegular,
  BuildingRegular,
  MailRegular,
  LaptopRegular,
  KeyRegular,
  DocumentRegular,
  ChevronDownRegular,
  ChevronUpRegular,
} from '@fluentui/react-icons';
import { apiClient } from '../api/client';
import { DATE_FORMAT } from '../config/constants';

const useStyles = makeStyles({
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    marginBottom: tokens.spacingVerticalXL,
  },
  tabs: {
    marginBottom: tokens.spacingVerticalL,
  },
  grid: {
    display: 'grid',
    gap: tokens.spacingVerticalL,
  },
  card: {
    padding: tokens.spacingVerticalL,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: tokens.spacingVerticalM,
  },
  cardTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  section: {
    marginTop: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalM,
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalS,
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: tokens.spacingVerticalS,
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  detailLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  detailValue: {
    fontSize: tokens.fontSizeBase300,
  },
  badgeList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
  },
  accessBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalL,
    paddingTop: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  emptyState: {
    textAlign: 'center',
    padding: tokens.spacingVerticalXXXL,
    color: tokens.colorNeutralForeground3,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
  },
  expandButton: {
    marginTop: tokens.spacingVerticalS,
  },
  hardwareList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
  notesBox: {
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    marginTop: tokens.spacingVerticalXS,
  },
  submittedInfo: {
    display: 'flex',
    gap: tokens.spacingHorizontalL,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    marginTop: tokens.spacingVerticalS,
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
});

interface AccessProvisioning {
  vpn: boolean;
  hrSystem: boolean;
  hubSpot: boolean;
  fileServer: boolean;
  sage: boolean;
}

interface HardwareConfig {
  isReused: boolean;
  serviceTag?: string;
  type?: 'Laptop' | 'Desktop';
  primaryDisplay: boolean;
  secondaryDisplay: boolean;
  mouse: boolean;
  keyboard: boolean;
  laptopBag: boolean;
}

interface OnboardingRequest {
  _id: string;
  requestId: string;
  firstName: string;
  lastName: string;
  displayName: string;
  jobTitle: string;
  department: string;
  role: string;
  mobilePhone?: string;
  businessPhone?: string;
  siteCode: string;
  lineManagerId: string;
  lineManagerName: string;
  lineManagerEmail: string;
  startDate: string;
  endDate?: string;
  accessProvisioning: AccessProvisioning;
  additionalSoftware: string[];
  hardware: HardwareConfig;
  additionalRequests?: string;
  submittedBy: string;
  submittedByEmail: string;
  submittedByName: string;
  submittedAt: string;
}

interface OffboardingRequest {
  _id: string;
  requestId: string;
  userId: string;
  userPrincipalName: string;
  userDisplayName: string;
  userEmail: string;
  department?: string;
  jobTitle?: string;
  terminationDate: string;
  lastWorkingDay?: string;
  removeMailbox: boolean;
  mailForwardingAddress?: string;
  oneDriveDelegate?: string;
  oneDriveDelegateName?: string;
  oneDriveDelegateEmail?: string;
  additionalNotes?: string;
  submittedBy: string;
  submittedByEmail: string;
  submittedByName: string;
  submittedAt: string;
}

interface HardwareRequest {
  _id: string;
  requestId: string;
  userId: string;
  userDisplayName: string;
  userPrincipalName: string;
  userEmail: string;
  managerId?: string;
  managerName?: string;
  managerEmail?: string;
  mainHardware: string[];
  peripherals: string[];
  monitorQuantity: number;
  justification: string;
  submittedBy: string;
  submittedByEmail: string;
  submittedByName: string;
  submittedAt: string;
}

type RequestType = 'onboarding' | 'offboarding' | 'hardware';

export function ApprovalPage() {
  const styles = useStyles();
  const [activeTab, setActiveTab] = useState<RequestType>('onboarding');
  const [onboarding, setOnboarding] = useState<OnboardingRequest[]>([]);
  const [offboarding, setOffboarding] = useState<OffboardingRequest[]>([]);
  const [hardware, setHardware] = useState<HardwareRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<string | null>(null);
  const [denyReason, setDenyReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

  const toggleExpanded = (id: string) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const fetchRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get<{
        data: { onboarding: OnboardingRequest[]; offboarding: OffboardingRequest[]; hardware: HardwareRequest[] };
      }>('/requests/pending');
      setOnboarding(response.data.onboarding);
      setOffboarding(response.data.offboarding);
      setHardware(response.data.hardware || []);
      // Auto-expand all cards for admin review
      const allIds = [
        ...response.data.onboarding.map(r => r._id),
        ...response.data.offboarding.map(r => r._id),
        ...(response.data.hardware || []).map(r => r._id),
      ];
      setExpandedCards(new Set(allIds));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleApprove = async (id: string, type: RequestType) => {
    setActionLoading(true);
    try {
      await apiClient.post(`/requests/${id}/approve`, { type });
      await fetchRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeny = async (id: string, type: RequestType) => {
    if (!denyReason.trim()) {
      setError('Please provide a reason for denial');
      return;
    }
    setActionLoading(true);
    try {
      await apiClient.post(`/requests/${id}/deny`, { type, reason: denyReason });
      setSelectedRequest(null);
      setDenyReason('');
      await fetchRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deny request');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', DATE_FORMAT);
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      ...DATE_FORMAT,
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const DetailItem = ({ label, value }: { label: string; value?: string | null }) => {
    if (!value) return null;
    return (
      <div className={styles.detailItem}>
        <Text className={styles.detailLabel}>{label}</Text>
        <Text className={styles.detailValue}>{value}</Text>
      </div>
    );
  };

  const renderOnboardingCard = (request: OnboardingRequest) => {
    const isExpanded = expandedCards.has(request._id);
    const accessItems = [];
    if (request.accessProvisioning?.vpn) accessItems.push('VPN');
    if (request.accessProvisioning?.hrSystem) accessItems.push('HR System');
    if (request.accessProvisioning?.hubSpot) accessItems.push('HubSpot');
    if (request.accessProvisioning?.fileServer) accessItems.push('File Server');
    if (request.accessProvisioning?.sage) accessItems.push('Sage');

    const hardwareItems = [];
    if (request.hardware?.primaryDisplay) hardwareItems.push('Primary Display');
    if (request.hardware?.secondaryDisplay) hardwareItems.push('Secondary Display');
    if (request.hardware?.mouse) hardwareItems.push('Mouse');
    if (request.hardware?.keyboard) hardwareItems.push('Keyboard');
    if (request.hardware?.laptopBag) hardwareItems.push('Laptop Bag');

    return (
      <Card key={request._id} className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.cardTitle}>
            <Text size={500} weight="semibold">{request.displayName}</Text>
            <Badge appearance="outline" color="informative">{request.role}</Badge>
          </div>
          <Badge appearance="filled" color="warning">{request.requestId}</Badge>
        </div>

        <Body1>{request.jobTitle} - {request.department}</Body1>

        <div className={styles.submittedInfo}>
          <span className={styles.metaItem}>
            <PersonRegular /> Submitted by: {request.submittedByName}
          </span>
          <span className={styles.metaItem}>
            <CalendarRegular /> {formatDateTime(request.submittedAt)}
          </span>
        </div>

        <Button
          appearance="subtle"
          icon={isExpanded ? <ChevronUpRegular /> : <ChevronDownRegular />}
          onClick={() => toggleExpanded(request._id)}
          className={styles.expandButton}
        >
          {isExpanded ? 'Hide Details' : 'Show Details'}
        </Button>

        {isExpanded && (
          <>
            <Divider style={{ margin: `${tokens.spacingVerticalM} 0` }} />

            {/* Employee Details */}
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <PersonRegular />
                <Text weight="semibold">Employee Details</Text>
              </div>
              <div className={styles.detailsGrid}>
                <DetailItem label="First Name" value={request.firstName} />
                <DetailItem label="Last Name" value={request.lastName} />
                <DetailItem label="Display Name" value={request.displayName} />
                <DetailItem label="Job Title" value={request.jobTitle} />
                <DetailItem label="Department" value={request.department} />
                <DetailItem label="Employment Type" value={request.role} />
                <DetailItem label="Site Code" value={request.siteCode} />
                <DetailItem label="Mobile Phone" value={request.mobilePhone} />
                <DetailItem label="Business Phone" value={request.businessPhone} />
              </div>
            </div>

            {/* Dates */}
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <CalendarRegular />
                <Text weight="semibold">Dates</Text>
              </div>
              <div className={styles.detailsGrid}>
                <DetailItem label="Start Date" value={formatDate(request.startDate)} />
                {request.endDate && (
                  <DetailItem label="End Date (Contract)" value={formatDate(request.endDate)} />
                )}
              </div>
            </div>

            {/* Line Manager */}
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <BuildingRegular />
                <Text weight="semibold">Line Manager</Text>
              </div>
              <div className={styles.detailsGrid}>
                <DetailItem label="Name" value={request.lineManagerName} />
                <DetailItem label="Email" value={request.lineManagerEmail} />
              </div>
            </div>

            {/* Access Provisioning */}
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <KeyRegular />
                <Text weight="semibold">Access Provisioning</Text>
              </div>
              {accessItems.length > 0 ? (
                <div className={styles.badgeList}>
                  {accessItems.map(item => (
                    <Badge key={item} appearance="tint" color="success">
                      <span className={styles.accessBadge}>
                        <CheckmarkRegular /> {item}
                      </span>
                    </Badge>
                  ))}
                </div>
              ) : (
                <Text style={{ color: tokens.colorNeutralForeground3 }}>No additional access requested</Text>
              )}
            </div>

            {/* Additional Software */}
            {request.additionalSoftware && request.additionalSoftware.length > 0 && (
              <div className={styles.section}>
                <div className={styles.sectionTitle}>
                  <DocumentRegular />
                  <Text weight="semibold">Additional Software</Text>
                </div>
                <div className={styles.badgeList}>
                  {request.additionalSoftware.map(sw => (
                    <Badge key={sw} appearance="outline">{sw}</Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Hardware */}
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <LaptopRegular />
                <Text weight="semibold">Hardware</Text>
              </div>
              <div className={styles.detailsGrid}>
                <DetailItem
                  label="Device"
                  value={request.hardware?.isReused
                    ? `Reused${request.hardware.serviceTag ? ` (${request.hardware.serviceTag})` : ''}`
                    : `New ${request.hardware?.type || 'Device'}`
                  }
                />
              </div>
              {hardwareItems.length > 0 && (
                <div className={styles.hardwareList} style={{ marginTop: tokens.spacingVerticalS }}>
                  {hardwareItems.map(item => (
                    <Badge key={item} appearance="outline" color="informative">{item}</Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Additional Requests */}
            {request.additionalRequests && (
              <div className={styles.section}>
                <div className={styles.sectionTitle}>
                  <DocumentRegular />
                  <Text weight="semibold">Additional Requests</Text>
                </div>
                <div className={styles.notesBox}>
                  <Text>{request.additionalRequests}</Text>
                </div>
              </div>
            )}
          </>
        )}

        <div className={styles.actions}>
          <Button
            appearance="primary"
            icon={<CheckmarkRegular />}
            onClick={() => handleApprove(request._id, 'onboarding')}
            disabled={actionLoading}
          >
            Approve
          </Button>
          <Dialog open={selectedRequest === request._id} onOpenChange={(_, data) => {
            if (!data.open) {
              setSelectedRequest(null);
              setDenyReason('');
            }
          }}>
            <DialogTrigger disableButtonEnhancement>
              <Button
                appearance="secondary"
                icon={<DismissRegular />}
                onClick={() => setSelectedRequest(request._id)}
              >
                Deny
              </Button>
            </DialogTrigger>
            <DialogSurface>
              <DialogBody>
                <DialogTitle>Deny Request</DialogTitle>
                <DialogContent>
                  <Field label="Reason for Denial" required>
                    <Textarea
                      value={denyReason}
                      onChange={(_, data) => setDenyReason(data.value)}
                      placeholder="Please provide a reason..."
                      rows={4}
                    />
                  </Field>
                </DialogContent>
                <DialogActions>
                  <DialogTrigger disableButtonEnhancement>
                    <Button appearance="secondary">Cancel</Button>
                  </DialogTrigger>
                  <Button
                    appearance="primary"
                    onClick={() => handleDeny(request._id, 'onboarding')}
                    disabled={actionLoading}
                  >
                    {actionLoading ? <Spinner size="tiny" /> : 'Deny Request'}
                  </Button>
                </DialogActions>
              </DialogBody>
            </DialogSurface>
          </Dialog>
        </div>
      </Card>
    );
  };

  const renderOffboardingCard = (request: OffboardingRequest) => {
    const isExpanded = expandedCards.has(request._id);

    return (
      <Card key={request._id} className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.cardTitle}>
            <Text size={500} weight="semibold">{request.userDisplayName}</Text>
          </div>
          <Badge appearance="filled" color="warning">{request.requestId}</Badge>
        </div>

        <Body1>{request.userEmail}</Body1>

        <div className={styles.submittedInfo}>
          <span className={styles.metaItem}>
            <PersonRegular /> Submitted by: {request.submittedByName}
          </span>
          <span className={styles.metaItem}>
            <CalendarRegular /> {formatDateTime(request.submittedAt)}
          </span>
        </div>

        <Button
          appearance="subtle"
          icon={isExpanded ? <ChevronUpRegular /> : <ChevronDownRegular />}
          onClick={() => toggleExpanded(request._id)}
          className={styles.expandButton}
        >
          {isExpanded ? 'Hide Details' : 'Show Details'}
        </Button>

        {isExpanded && (
          <>
            <Divider style={{ margin: `${tokens.spacingVerticalM} 0` }} />

            {/* Employee Details */}
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <PersonRegular />
                <Text weight="semibold">Employee Details</Text>
              </div>
              <div className={styles.detailsGrid}>
                <DetailItem label="Display Name" value={request.userDisplayName} />
                <DetailItem label="Email" value={request.userEmail} />
                <DetailItem label="Principal Name" value={request.userPrincipalName} />
                <DetailItem label="Department" value={request.department} />
                <DetailItem label="Job Title" value={request.jobTitle} />
              </div>
            </div>

            {/* Dates */}
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <CalendarRegular />
                <Text weight="semibold">Dates</Text>
              </div>
              <div className={styles.detailsGrid}>
                <DetailItem label="Termination Date" value={formatDate(request.terminationDate)} />
                {request.lastWorkingDay && (
                  <DetailItem label="Last Working Day" value={formatDate(request.lastWorkingDay)} />
                )}
              </div>
            </div>

            {/* Data Handling */}
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <MailRegular />
                <Text weight="semibold">Data Handling</Text>
              </div>
              <div className={styles.detailsGrid}>
                <DetailItem
                  label="Mailbox Action"
                  value={request.removeMailbox ? 'Remove mailbox' : 'Keep mailbox'}
                />
                {request.mailForwardingAddress && (
                  <DetailItem label="Forward Mail To" value={request.mailForwardingAddress} />
                )}
              </div>
              {request.oneDriveDelegateName && (
                <div className={styles.detailsGrid} style={{ marginTop: tokens.spacingVerticalS }}>
                  <DetailItem label="OneDrive Delegate" value={request.oneDriveDelegateName} />
                  <DetailItem label="Delegate Email" value={request.oneDriveDelegateEmail} />
                </div>
              )}
            </div>

            {/* Additional Notes */}
            {request.additionalNotes && (
              <div className={styles.section}>
                <div className={styles.sectionTitle}>
                  <DocumentRegular />
                  <Text weight="semibold">Additional Notes</Text>
                </div>
                <div className={styles.notesBox}>
                  <Text>{request.additionalNotes}</Text>
                </div>
              </div>
            )}
          </>
        )}

        <div className={styles.actions}>
          <Button
            appearance="primary"
            icon={<CheckmarkRegular />}
            onClick={() => handleApprove(request._id, 'offboarding')}
            disabled={actionLoading}
          >
            Approve
          </Button>
          <Dialog open={selectedRequest === request._id} onOpenChange={(_, data) => {
            if (!data.open) {
              setSelectedRequest(null);
              setDenyReason('');
            }
          }}>
            <DialogTrigger disableButtonEnhancement>
              <Button
                appearance="secondary"
                icon={<DismissRegular />}
                onClick={() => setSelectedRequest(request._id)}
              >
                Deny
              </Button>
            </DialogTrigger>
            <DialogSurface>
              <DialogBody>
                <DialogTitle>Deny Request</DialogTitle>
                <DialogContent>
                  <Field label="Reason for Denial" required>
                    <Textarea
                      value={denyReason}
                      onChange={(_, data) => setDenyReason(data.value)}
                      placeholder="Please provide a reason..."
                      rows={4}
                    />
                  </Field>
                </DialogContent>
                <DialogActions>
                  <DialogTrigger disableButtonEnhancement>
                    <Button appearance="secondary">Cancel</Button>
                  </DialogTrigger>
                  <Button
                    appearance="primary"
                    onClick={() => handleDeny(request._id, 'offboarding')}
                    disabled={actionLoading}
                  >
                    {actionLoading ? <Spinner size="tiny" /> : 'Deny Request'}
                  </Button>
                </DialogActions>
              </DialogBody>
            </DialogSurface>
          </Dialog>
        </div>
      </Card>
    );
  };

  const renderHardwareCard = (request: HardwareRequest) => {
    const isExpanded = expandedCards.has(request._id);

    return (
      <Card key={request._id} className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.cardTitle}>
            <Text size={500} weight="semibold">{request.userDisplayName}</Text>
            <Badge appearance="outline" color="brand">Hardware</Badge>
          </div>
          <Badge appearance="filled" color="warning">{request.requestId}</Badge>
        </div>

        <Body1>{request.userEmail}</Body1>

        <div className={styles.submittedInfo}>
          <span className={styles.metaItem}>
            <PersonRegular /> Submitted by: {request.submittedByName}
          </span>
          <span className={styles.metaItem}>
            <CalendarRegular /> {formatDateTime(request.submittedAt)}
          </span>
        </div>

        <Button
          appearance="subtle"
          icon={isExpanded ? <ChevronUpRegular /> : <ChevronDownRegular />}
          onClick={() => toggleExpanded(request._id)}
          className={styles.expandButton}
        >
          {isExpanded ? 'Hide Details' : 'Show Details'}
        </Button>

        {isExpanded && (
          <>
            <Divider style={{ margin: `${tokens.spacingVerticalM} 0` }} />

            {/* Employee Details */}
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <PersonRegular />
                <Text weight="semibold">Beneficiary Details</Text>
              </div>
              <div className={styles.detailsGrid}>
                <DetailItem label="Employee Name" value={request.userDisplayName} />
                <DetailItem label="Email" value={request.userEmail} />
                <DetailItem label="User Principal Name" value={request.userPrincipalName} />
                {request.managerName && <DetailItem label="Line Manager" value={request.managerName} />}
              </div>
            </div>

            {/* Hardware Requested */}
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <LaptopRegular />
                <Text weight="semibold">Requested Hardware</Text>
              </div>

              {request.mainHardware.length > 0 && (
                <div style={{ marginBottom: tokens.spacingVerticalS }}>
                  <Text className={styles.detailLabel}>Main Hardware:</Text>
                  <div className={styles.hardwareList}>
                    {request.mainHardware.map((item) => (
                      <Badge key={item} appearance="filled" color="brand">{item}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {request.peripherals.length > 0 && (
                <div style={{ marginBottom: tokens.spacingVerticalS }}>
                  <Text className={styles.detailLabel}>Peripherals:</Text>
                  <div className={styles.hardwareList}>
                    {request.peripherals.map((item) => (
                      <Badge key={item} appearance="filled" color="informative">{item}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {request.monitorQuantity > 0 && (
                <div style={{ marginBottom: tokens.spacingVerticalS }}>
                  <Text className={styles.detailLabel}>Monitors:</Text>
                  <Text className={styles.detailValue}>
                    {request.monitorQuantity} monitor{request.monitorQuantity > 1 ? 's' : ''}
                  </Text>
                </div>
              )}
            </div>

            {/* Justification */}
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <DocumentRegular />
                <Text weight="semibold">Business Justification</Text>
              </div>
              <div className={styles.notesBox}>
                <Text>{request.justification}</Text>
              </div>
            </div>

            <div className={styles.actions}>
              <Button
                appearance="primary"
                icon={<CheckmarkRegular />}
                onClick={() => handleApprove(request._id, 'hardware')}
                disabled={actionLoading}
              >
                Approve
              </Button>

              <Dialog>
                <DialogTrigger disableButtonEnhancement>
                  <Button
                    appearance="secondary"
                    icon={<DismissRegular />}
                    onClick={() => setSelectedRequest(request._id)}
                    disabled={actionLoading}
                  >
                    Deny
                  </Button>
                </DialogTrigger>
                <DialogSurface>
                  <DialogBody>
                    <DialogTitle>Deny Hardware Request</DialogTitle>
                    <DialogContent>
                      <Text>Please provide a reason for denying this hardware request:</Text>
                      <Field style={{ marginTop: tokens.spacingVerticalM }}>
                        <Textarea
                          placeholder="Enter reason for denial..."
                          value={denyReason}
                          onChange={(_, data) => setDenyReason(data.value)}
                          rows={4}
                        />
                      </Field>
                    </DialogContent>
                    <DialogActions>
                      <DialogTrigger disableButtonEnhancement>
                        <Button appearance="secondary">Cancel</Button>
                      </DialogTrigger>
                      <Button
                        appearance="primary"
                        onClick={() => handleDeny(request._id, 'hardware')}
                        disabled={actionLoading}
                      >
                        {actionLoading ? <Spinner size="tiny" /> : 'Deny Request'}
                      </Button>
                    </DialogActions>
                  </DialogBody>
                </DialogSurface>
              </Dialog>
            </div>
          </>
        )}
      </Card>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title2>Awaiting Approval</Title2>
        <Body1 style={{ color: tokens.colorNeutralForeground3 }}>
          Review and approve pending onboarding, offboarding, and hardware requests
        </Body1>
      </div>

      {error && (
        <MessageBar intent="error" style={{ marginBottom: tokens.spacingVerticalL }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      <TabList
        className={styles.tabs}
        selectedValue={activeTab}
        onTabSelect={(_, data) => setActiveTab(data.value as RequestType)}
      >
        <Tab value="onboarding">
          Onboarding ({onboarding.length})
        </Tab>
        <Tab value="offboarding">
          Offboarding ({offboarding.length})
        </Tab>
        <Tab value="hardware">
          Hardware ({hardware.length})
        </Tab>
      </TabList>

      {loading ? (
        <div className={styles.loading}>
          <Spinner size="large" label="Loading requests..." />
        </div>
      ) : (
        <div className={styles.grid}>
          {activeTab === 'onboarding' ? (
            onboarding.length > 0 ? (
              onboarding.map(renderOnboardingCard)
            ) : (
              <div className={styles.emptyState}>
                <CheckmarkRegular style={{ fontSize: '48px', marginBottom: tokens.spacingVerticalM }} />
                <Text>No pending onboarding requests</Text>
              </div>
            )
          ) : activeTab === 'offboarding' ? (
            offboarding.length > 0 ? (
              offboarding.map(renderOffboardingCard)
            ) : (
              <div className={styles.emptyState}>
                <CheckmarkRegular style={{ fontSize: '48px', marginBottom: tokens.spacingVerticalM }} />
                <Text>No pending offboarding requests</Text>
              </div>
            )
          ) : (
            hardware.length > 0 ? (
              hardware.map(renderHardwareCard)
            ) : (
              <div className={styles.emptyState}>
                <CheckmarkRegular style={{ fontSize: '48px', marginBottom: tokens.spacingVerticalM }} />
                <Text>No pending hardware requests</Text>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}
